
import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import { UserPlus, ArrowLeft, RefreshCw, AlertCircle, Shield, Mail, Smartphone, Lock, Hash, Store } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { staffAPI, storeAPI } from "../../api/apiService";
import { NotificationContext } from "../../context/NotificationContext";
import { useAuth } from "../../context/AuthContext";

export default function AddEmployee() {
    const navigate = useNavigate();
    const { addNotification } = useContext(NotificationContext);
    const { user, refreshProfile } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [stores, setStores] = useState([]);

    const [formData, setFormData] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        referral_id: "",
        password: "",
        role: "SALES_EXECUTIVE",
        store: ""
    });

    const activeStaffCount = user?.get_staff_count || 0;
    const maxStaff = parseInt(user?.max_staff_allowed || 0);
    const isLimitReached = maxStaff !== -1 && activeStaffCount >= maxStaff;

    const fetchStores = async () => {
        try {
            const res = await storeAPI.getAll();
            setStores(res.data || []);
            if (res.data && res.data.length > 0) {
                setFormData(prev => ({ ...prev, store: res.data[0].id }));
            }
        } catch (err) {
            console.error("Failed to load stores:", err);
        }
    };

    useEffect(() => {
        refreshProfile();
        fetchStores();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
    
        try {
            await staffAPI.createStaff(formData);
            addNotification("Employee added successfully!", "success");
            navigate("/employees");
        } catch (err) {
            console.error("Staff Creation Error:", err.response?.data);
            const data = err.response?.data;
            let errorMessage = "Failed to create employee";
            
            if (data?.detail) {
                if (typeof data.detail === 'object') {
                    // Handle structured validation errors
                    const fields = Object.keys(data.detail);
                    errorMessage = fields.map(field => {
                        const messages = data.detail[field];
                        return `${field.replace('_', ' ')}: ${Array.isArray(messages) ? messages[0] : messages}`;
                    }).join(", ");
                } else {
                    errorMessage = data.detail;
                }
            }
            
            setError(errorMessage);
            addNotification(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-4 md:p-6">
            <div className="max-w-7xl mx-auto space-y-6">
                
                {/* Back Button */}
                <button 
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors font-bold"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Back to List
                </button>

                {/* Header */}
                <div className="bg-white dark:bg-slate-800 p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700">
                    <div className="flex items-center gap-5">
                        <div className="p-4 bg-green-600 rounded-2xl text-white shadow-lg shadow-green-500/20">
                            <UserPlus className="w-10 h-10" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-black text-gray-900 dark:text-white">Add New Employee</h1>
                            <p className="text-gray-500 dark:text-slate-400 font-medium">Create a new access account for your team</p>
                        </div>
                    </div>
                </div>

                {isLimitReached && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 p-6 rounded-3xl flex items-start gap-4">
                        <AlertCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                            <h4 className="font-bold text-amber-900 dark:text-amber-400">Staff Limit Reached</h4>
                            <p className="text-sm text-amber-700 dark:text-amber-500/80 mt-1">
                                You have reached the maximum staff members allowed for your current plan ({maxStaff}). 
                                Please upgrade your subscription to add more members.
                            </p>
                        </div>
                    </div>
                )}

                {/* Form Card */}
                <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                    <form onSubmit={handleSubmit} className="p-8 md:p-10 space-y-8">
                        {error && (
                            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 text-red-600 dark:text-red-400 rounded-2xl flex items-center gap-3 text-sm font-bold">
                                <AlertCircle className="w-5 h-5" />
                                {error}
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InputField 
                                label="First Name"
                                icon={Shield}
                                value={formData.first_name}
                                onChange={v => setFormData({...formData, first_name: v})}
                                placeholder="Enter first name"
                                required
                                disabled={isLimitReached}
                            />
                            <InputField 
                                label="Last Name"
                                icon={Shield}
                                value={formData.last_name}
                                onChange={v => setFormData({...formData, last_name: v})}
                                placeholder="Enter last name"
                                required
                                disabled={isLimitReached}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InputField 
                                label="Email Address"
                                type="email"
                                icon={Mail}
                                value={formData.email}
                                onChange={v => setFormData({...formData, email: v})}
                                placeholder="employee@company.com"
                                required
                                disabled={isLimitReached}
                            />
                            <InputField 
                                label="Phone Number"
                                type="tel"
                                icon={Smartphone}
                                value={formData.phone}
                                onChange={v => setFormData({...formData, phone: v})}
                                placeholder="10-digit mobile number"
                                required
                                disabled={isLimitReached}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             <InputField 
                                label="Initial Password"
                                type="password"
                                icon={Lock}
                                value={formData.password}
                                onChange={v => setFormData({...formData, password: v})}
                                placeholder="Create a strong password"
                                required
                                disabled={isLimitReached}
                            />
                            <InputField 
                                label="Referral ID"
                                icon={Hash}
                                value={formData.referral_id}
                                onChange={v => setFormData({...formData, referral_id: v})}
                                placeholder="Enter referral ID"
                                disabled={isLimitReached}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 ml-1">Assigned Role</label>
                                <div className="relative">
                                    <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <select 
                                        className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-slate-900 border-none rounded-2xl text-sm font-bold appearance-none dark:text-white outline-none ring-offset-2 focus:ring-2 focus:ring-green-500 transition-all"
                                        value={formData.role}
                                        onChange={(e) => setFormData({...formData, role: e.target.value})}
                                        disabled={isLimitReached}
                                    >
                                        <option value="SALES_EXECUTIVE">Sales Executive</option>
                                        <option value="MANAGER">Manager</option>
                                        <option value="ACCOUNTANT">Accountant</option>
                                        <option value="INVENTORY_STAFF">Inventory Staff</option>
                                        <option value="BILLING_STAFF">Billing Staff</option>
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 ml-1">Assigned Store</label>
                                <div className="relative">
                                    <Store className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <select 
                                        className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-slate-900 border-none rounded-2xl text-sm font-bold appearance-none dark:text-white outline-none ring-offset-2 focus:ring-2 focus:ring-green-500 transition-all"
                                        value={formData.store}
                                        onChange={(e) => setFormData({...formData, store: e.target.value})}
                                        disabled={isLimitReached}
                                        required
                                    >
                                        <option value="">Select a Store</option>
                                        {stores.map(store => (
                                            <option key={store.id} value={store.id}>{store.name} ({store.city})</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="pt-6">
                            <button
                                type="submit"
                                disabled={loading || isLimitReached}
                                className="w-full flex items-center justify-center gap-3 py-4 bg-green-600 text-white font-black text-lg rounded-2xl shadow-xl shadow-green-500/20 hover:bg-green-700 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? <RefreshCw className="w-6 h-6 animate-spin" /> : <UserPlus className="w-6 h-6" />}
                                {loading ? "Creating Employee Account..." : "Create Employee Account"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

function InputField({ label, icon: Icon, value, onChange, type = "text", placeholder, required, disabled }) {
    return (
        <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-wider text-gray-500 ml-1">{label} {required && "*"}</label>
            <div className="relative group">
                {Icon && <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 transition-colors group-focus-within:text-green-500" />}
                <input 
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    required={required}
                    disabled={disabled}
                    className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-slate-900 border-none rounded-2xl text-sm font-bold dark:text-white outline-none ring-offset-2 focus:ring-2 focus:ring-green-500 transition-all"
                />
            </div>
        </div>
    );
}
